import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { db, auth } from '../../../firebase/config';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { setDocWithCreatedBy } from '../../../firebase/utils';
import { getTimestamp } from 'common/utils';
import { CashRegisterState, CashMovement, CashMovementType } from 'common/types';
import styles from './cashRegister.module.css';
import { appContext } from 'common/contexts/appContext';

type CashRegisterOpenProps = {
  isOpen: boolean;
  onClose: () => void;
};

const CashRegisterOpen: React.FC<CashRegisterOpenProps> = ({ isOpen, onClose }) => {
  const [t] = useTranslation();
  const [initialAmount, setInitialAmount] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasActiveRegister, setHasActiveRegister] = useState<boolean>(false);
  const [lastClosedRegister, setLastClosedRegister] = useState<CashRegisterState | null>(null);
  const { state } = useContext(appContext);

  useEffect(() => {
    if (isOpen) {
      checkActiveRegister();
      fetchLastClosedRegister();
    } else {
      // Reset form when dialog closes
      setInitialAmount('');
      setNotes('');
      setError(null);
    }
  }, [isOpen]);

  const checkActiveRegister = async () => {
    setLoading(true);
    setError(null);
    try {
      const cashRegisterCollection = collection(db, 'cashRegister');
      const q = query(
        cashRegisterCollection,
        where('parentUserId', '==', state.parentId),
        where('isOpen', '==', true),
        orderBy('openDate', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      setHasActiveRegister(!querySnapshot.empty);
    } catch (err) {
      console.error('Error checking active register:', err);
      setError(t('terminal.cashRegister.errorChecking', 'Error al verificar el estado de la caja'));
    } finally {
      setLoading(false);
    }
  };

  const fetchLastClosedRegister = async () => {
    try {
      const cashRegisterCollection = collection(db, 'cashRegister');
      const q = query(
        cashRegisterCollection,
        where('parentUserId', '==', state.parentId),
        where('isOpen', '==', false),
        orderBy('closeDate', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const registerData = querySnapshot.docs[0].data() as CashRegisterState;
        registerData.id = querySnapshot.docs[0].id;
        setLastClosedRegister(registerData);
      }
    } catch (err) {
      console.error('Error fetching last closed register:', err);
    }
  };

  const handleSubmit = async () => {
    if (!initialAmount.trim()) {
      setError(t('terminal.cashRegister.amountRequired', 'El monto inicial es requerido'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const amount = parseFloat(initialAmount);
      if (isNaN(amount) || amount < 0) {
        throw new Error(t('terminal.cashRegister.invalidAmount', 'Monto inválido'));
      }
      const optimistFunction = async () => {
        // Create new cash register state
        const cashRegisterCollection = collection(db, 'cashRegister');
        const registerState: Omit<CashRegisterState, 'id'> = {
          isOpen: true,
          openDate: getTimestamp(),
          initialAmount: amount,
          currentAmount: amount,
          userId: auth.currentUser?.uid || null,
        };

        // Save to Firestore using setDocWithCreatedBy
        const registerDoc = await setDocWithCreatedBy(cashRegisterCollection, registerState);

        // Create initial cash movement
        const cashMovementsCollection = collection(db, 'cashMovements');
        const movement: Omit<CashMovement, 'id'> = {
          type: CashMovementType.INITIAL,
          amount,
          date: getTimestamp(),
          notes: notes.trim(),
          userId: auth.currentUser?.uid || null,
        };

        await setDocWithCreatedBy(cashMovementsCollection, movement);
      };

      optimistFunction();
      onClose();
    } catch (err) {
      console.error('Error opening cash register:', err);
      setError(
        err instanceof Error
          ? err.message
          : t('terminal.cashRegister.errorOpening', 'Error al abrir la caja')
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.dialog}>
        <div className={`${styles.header} ${styles.headerAddition}`}>
          <div className={styles.headerIcon}>🔓</div>
          <h2>{t('terminal.cashRegister.openTitle', 'Apertura de Caja')}</h2>
        </div>

        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>{t('common.loading', 'Cargando...')}</div>
          ) : hasActiveRegister ? (
            <div className={styles.message}>
              {t('terminal.cashRegister.alreadyOpen', 'Ya hay una caja abierta. Debe cerrarla antes de abrir una nueva.')}
            </div>
          ) : (
            <>
              {error && <div className={styles.error}>{error}</div>}

              {lastClosedRegister && (
                <div className={styles.lastClosedInfo}>
                  <h3>{t('terminal.cashRegister.lastClosed', 'Último Cierre de Caja')}</h3>
                  <div className={styles.lastClosedDetails}>
                    <div className={styles.lastClosedItem}>
                      <span>{t('terminal.cashRegister.closedAt', 'Cerrada el')}:</span>
                      <span>{formatDate(lastClosedRegister.closeDate || 0)}</span>
                    </div>
                    <div className={styles.lastClosedItem}>
                      <span>{t('terminal.cashRegister.finalAmount', 'Monto Final')}:</span>
                      <span>{formatCurrency(lastClosedRegister.currentAmount)}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.formGroup}>
                <label htmlFor="initialAmount">
                  {t('terminal.cashRegister.initialAmount', 'Monto Inicial')}:
                </label>
                <div className={styles.amountInputContainer}>
                  <span className={styles.currencySymbol}>$</span>
                  <input
                    id="initialAmount"
                    type="number"
                    value={initialAmount}
                    onChange={(e) => setInitialAmount(e.target.value)}
                    placeholder="0.00"
                    className={styles.input}
                    min="0"
                    step="0.01"
                    autoFocus
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="notes">
                  {t('terminal.cashRegister.notes', 'Notas')}:
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('terminal.cashRegister.notesPlaceholder', 'Observaciones (opcional)')}
                  className={styles.textarea}
                  rows={3}
                />
              </div>
            </>
          )}
        </div>

        <div className={styles.actions}>
          <button
            className={styles.cancelButton}
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel', 'Cancelar')}
          </button>
          {!hasActiveRegister && (
            <button
              className={`${styles.submitButton} ${styles.addButton}`}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading
                ? t('common.processing', 'Procesando...')
                : t('terminal.cashRegister.open', 'Abrir Caja')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CashRegisterOpen;
